import { Button, Group, Tabs } from "@mantine/core";
import { useEffect } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormFieldController } from "../../../../../../components/BuildingBlocks/Forms/Controllers/FormFieldController";
import { countErrorsForTab } from "../../../../../../components/BuildingBlocks/Forms/utils/countErrorsForTab";
import { setErrorsFromResponseData } from "../../../../../../components/BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import { HistoryTab } from "../../../../../../components/HistoryTab/HistoryTab";
import { TabWithHighlightAndError } from "../../../../../../mantine/components/TabWithHighlightAndError/TabWithHighlightAndError";
import { useToast } from "../../../../../../mantine/notifications/useToast";
import { buildFieldNameToLabelMap } from "../../../../../../utils/react-hook-form/buildFieldNameToLabelMap";
import type {
  Paragraph6CompanyResponse,
  Paragraph6ContractFormFields,
  Paragraph6ContractPayload,
  Paragraph6ContractResponse
} from "../../../../Paragraph6.types";
import {
  PARAGRAPH_6_CONTRACT_FORM_INPUT_DATA,
  type PARAGRAPH_6_CONTRACT_FORM_TABS,
  TABS
} from "./Paragraph6ContractForm.constants";
import { Beteiligungen } from "./Tabs/Beteiligungen";
import { Fristen } from "./Tabs/Fristen";
import { GeneralInformation } from "./Tabs/GeneralInformation";

interface Paragraph6ContractFormProps {
  paragraph6Contract?: Partial<Paragraph6ContractResponse>;
  paragraph6Companies: Array<Paragraph6CompanyResponse>;
  isStaff?: boolean;
  isSubmitting?: boolean;
  isNewContractWithoutReference?: boolean;
  submissionError?: Error;
  onSubmit: SubmitHandler<Paragraph6ContractPayload>;
  onCancel: () => void;
  onContractNameChange: (name: string) => void;
}

const fieldNameToLabelMap = buildFieldNameToLabelMap(
  PARAGRAPH_6_CONTRACT_FORM_INPUT_DATA
);

function Paragraph6ContractForm({
  paragraph6Contract,
  paragraph6Companies,
  isStaff,
  isSubmitting = false,
  isNewContractWithoutReference = false,
  submissionError,
  onSubmit,
  onCancel,
  onContractNameChange
}: Paragraph6ContractFormProps) {
  const { showErrorToast } = useToast();
  const { t } = useTranslation();

  const paragraph6ContractFormFields: Partial<Paragraph6ContractFormFields> = {
    ...paragraph6Contract,
    has_split_beteiligung:
      isNewContractWithoutReference ||
      !!paragraph6Contract?.beteiligung_nicht_erstattungsfaehig,
    has_fiktive_strommenge:
      !!paragraph6Contract?.beteiligung_fiktive_strommengen
  };

  const editMode = !!paragraph6Contract?.id;

  const {
    control,
    formState: { errors },
    handleSubmit,
    setError,
    watch,
    reset
  } = useForm<Paragraph6ContractFormFields>({
    defaultValues: paragraph6ContractFormFields ?? {}
  });

  useEffect(() => {
    if (submissionError) {
      setErrorsFromResponseData<Paragraph6ContractPayload>(
        showErrorToast,
        submissionError,
        watch(),
        setError,
        t("errors.UnknownError")
      );
    }
  }, [setError, submissionError, t, watch, showErrorToast]);

  const watchedContractName = watch("name");
  useEffect(() => {
    if (onContractNameChange && editMode) {
      onContractNameChange(watchedContractName || "");
    }
  }, [watchedContractName, onContractNameChange, editMode]);

  return (
    <form
      className="Paragraph6ContractForm"
      noValidate
      onSubmit={handleSubmit(onSubmit)}
    >
      <Tabs defaultValue={TABS[0].value}>
        <Tabs.List>
          {TABS.filter((tab) => editMode || !tab.hiddenInCreateMode).map(
            (tab) => (
              <TabWithHighlightAndError
                key={tab.value}
                numErrors={countErrorsForTab(
                  tab.value as PARAGRAPH_6_CONTRACT_FORM_TABS,
                  PARAGRAPH_6_CONTRACT_FORM_INPUT_DATA,
                  errors
                )}
                value={tab.value}
              >
                {tab.label}
              </TabWithHighlightAndError>
            )
          )}
        </Tabs.List>
        <Tabs.Panel value={TABS[0].value}>
          <GeneralInformation
            control={control}
            paragraph6Companies={paragraph6Companies}
            paragraph6Contract={paragraph6Contract}
          />
        </Tabs.Panel>
        <Tabs.Panel value={TABS[1].value}>
          <Fristen control={control} isStaff={isStaff} />
        </Tabs.Panel>
        <Tabs.Panel value={TABS[2].value}>
          <Beteiligungen
            control={control}
            errors={errors}
            isStaff={isStaff}
            watch={watch}
          />
        </Tabs.Panel>
        <Tabs.Panel value={TABS[3].value}>
          <section>
            <FormFieldController
              control={control}
              data={PARAGRAPH_6_CONTRACT_FORM_INPUT_DATA.notes}
              error={errors.notes}
            />
          </section>
        </Tabs.Panel>
        <Tabs.Panel value={TABS[4].value}>
          {paragraph6Contract?.id && (
            <HistoryTab
              entityId={paragraph6Contract.id}
              entityType="paragraph6Contract"
              fieldNameToLabelMap={fieldNameToLabelMap}
            />
          )}
        </Tabs.Panel>
      </Tabs>
      <Group justify="flex-end" my="md">
        <Button
          disabled={isSubmitting}
          variant="default"
          onClick={() => {
            reset();
            onCancel();
          }}
        >
          Abbrechen
        </Button>
        <Button color="primary" loading={isSubmitting} type="submit">
          Speichern
        </Button>
      </Group>
    </form>
  );
}

export { Paragraph6ContractForm, Paragraph6ContractFormProps };
